import { Component, effect, inject, input } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FasciaOraria } from '../../calendar/calendar.utils';
import { HolidaysService } from '../../calendar/services/holidays.service';
import { DatepickerFieldComponent } from '../datepicker-field/datepicker-field.component';
import {
  buildHolidayFilter,
  latestHHmm,
  ultimoInizioHHmm,
} from '../event-date-time-form.utils';
import { TimepickerFieldComponent } from '../timepicker-field/timepicker-field.component';
import { DateTimeRangeController } from './date-time-range.controller';

/**
 * Start/end date+time fields for a range (assenza/turno), together with the
 * reactive min-date/min-time wiring (`DateTimeRangeController`) and holiday
 * filtering shared by the "add assenza" and "add turno" dialogs. The
 * consumer only owns the FormGroup (dateStart/timeStart/dateEnd/timeEnd
 * controls); this component owns everything about how the fields behave.
 */
@UntilDestroy()
@Component({
  selector: 'siat-date-time-range-fields',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    DatepickerFieldComponent,
    TimepickerFieldComponent,
  ],
  templateUrl: './date-time-range-fields.component.html',
})
export class DateTimeRangeFieldsComponent {
  /**
   * The formgroup
   */
  readonly formGroup = input.required<FormGroup>();

  /**
   * Label/placeholder for the start date field.
   */
  readonly startLabel = input('Data inizio');

  /**
   * Label/placeholder for the end date field.
   */
  readonly endLabel = input('Data fine');

  /**
   * Opening window of the structure, limiting the selectable times.
   */
  readonly fasciaOraria = input<Pick<FasciaOraria, 'inizio' | 'fine'> | null>(
    null
  );

  /**
   * Holidays service
   */
  private readonly holidaysService = inject(HolidaysService);

  /**
   * Range controller
   */
  private rangeController: DateTimeRangeController | null = null;

  /**
   * Holiday filter for the datepickers.
   */
  readonly holidayFilter = buildHolidayFilter(this.holidaysService);

  constructor() {
    effect(() => {
      this.rangeController = new DateTimeRangeController(
        this.formGroup(),
        this
      );
    });
  }

  /**
   * @return {Date} minimum selectable start date.
   */
  get minDateStart(): Date {
    return this.rangeController?.minDateStart ?? new Date();
  }

  /**
   * @return {Date} minimum selectable end date.
   */
  get minDateEnd(): Date {
    return this.rangeController?.minDateEnd ?? new Date();
  }

  /**
   * @return {string} minimum selectable start time ('HH:mm'), never before the opening time.
   */
  get minTimeStart(): string {
    return latestHHmm(
      this.rangeController?.minTimeStart,
      this.fasciaOraria()?.inizio
    );
  }

  /**
   * @return {string} minimum selectable end time ('HH:mm'), never before the opening time.
   */
  get minTimeEnd(): string {
    return latestHHmm(
      this.rangeController?.minTimeEnd ?? '00:00',
      this.fasciaOraria()?.inizio
    );
  }

  /**
   * @return {string | null} maximum selectable time ('HH:mm'): the closing time, if any.
   */
  get maxTime(): string | null {
    return this.fasciaOraria()?.fine ?? null;
  }

  /**
   * @return {string | null} maximum selectable start time ('HH:mm'): one step before closing, if any.
   */
  get maxTimeStart(): string | null {
    const fine = this.fasciaOraria()?.fine;
    return fine ? ultimoInizioHHmm(fine) : null;
  }
}
